import { subMinutes, subHours, subDays } from 'date-fns';

export interface DemoWebhookEvent {
  id: string;
  title: string;
  description: string;
  activity_type: 'webhook_success' | 'webhook_error' | 'webhook_received';
  icon: string;
  created_at: string;
  recipient?: string;
  status?: string;
  reason?: string;
}

const generateRandomTime = (minutesAgo: number) => 
  subMinutes(new Date(), minutesAgo).toISOString();

const generateRandomHour = (hoursAgo: number) => 
  subHours(new Date(), hoursAgo).toISOString();

// Generate demo webhook events
const generateDemoWebhookEvents = (): DemoWebhookEvent[] => {
  const events: DemoWebhookEvent[] = [];
  
  // Lead Category Webhook Processed events (success)
  const leadCategoryEvents = [
    {
      recipient: 'aisling.pell...',
      status: 'Out Of Office',
      reason: 'Auto-reply detected'
    },
    {
      recipient: 'luke...',
      status: 'Sender Originated Bounce',
      reason: 'Invalid email address'
    },
    {
      recipient: 'melissa.watson...',
      status: 'Sender Originated Bounce',
      reason: 'Mailbox does not exist'
    },
    {
      recipient: 'sabrina...',
      status: 'Sender Originated Bounce',
      reason: 'Domain not found'
    },
    {
      recipient: 'david.kim...',
      status: 'Positive Reply',
      reason: 'Interested in partnership'
    },
    {
      recipient: 'emma.davis...',
      status: 'Question',
      reason: 'Requesting more information'
    },
    {
      recipient: 'michael.chen...',
      status: 'Not Interested',
      reason: 'Declined offer'
    },
    {
      recipient: 'sarah.wilson...',
      status: 'Meeting Scheduled',
      reason: 'Calendar link clicked'
    }
  ];

  // Generate Lead Category Webhook Processed events
  leadCategoryEvents.forEach((event, index) => {
    events.push({
      id: `webhook-lead-${index + 1}`,
      title: 'Lead Category Webhook Processed',
      description: `To: ${event.recipient} | ${event.status}`,
      activity_type: 'webhook_success',
      icon: 'check-circle',
      created_at: generateRandomTime(5 + index * 2), // 5, 7, 9, 11, 13, 15, 17, 19 minutes ago
      recipient: event.recipient,
      status: event.status,
      reason: event.reason
    });
  });

  // Email Sent Webhook events
  const emailSentEvents = [
    {
      recipient: 'alex.johnson@techventures.io',
      campaign: 'Q4 Enterprise Outreach',
      subject: 'Partnership opportunity with NorthStar Analytics'
    },
    {
      recipient: 'emma.davis@innovatedesign.com',
      campaign: 'Product Launch - Series B Targets',
      subject: 'Revolutionary marketing automation platform'
    },
    {
      recipient: 'michael.chen@acmecorp.com',
      campaign: 'AI-Driven Insights Campaign',
      subject: 'AI-driven insights for your business growth'
    },
    {
      recipient: 'sarah.wilson@summittech.io',
      campaign: 'Strategic Partnership Development',
      subject: 'Strategic partnership opportunity'
    }
  ];

  emailSentEvents.forEach((event, index) => {
    events.push({
      id: `webhook-sent-${index + 1}`,
      title: 'Email Sent Webhook Processed',
      description: `To: ${event.recipient} | Campaign: ${event.campaign}`,
      activity_type: 'webhook_success',
      icon: 'send',
      created_at: generateRandomTime(10 + index * 3), // 10, 13, 16, 19 minutes ago
      recipient: event.recipient,
      status: 'Sent',
      reason: event.subject
    });
  });

  // Email Reply Webhook events
  const emailReplyEvents = [
    {
      recipient: 'sarah.chen@northstaranalytics.com',
      from: 'alex.johnson@techventures.io',
      replyType: 'Positive Reply',
      subject: 'Re: Partnership opportunity with NorthStar Analytics'
    },
    {
      recipient: 'marcus.rodriguez@velocitymarketing.com',
      from: 'emma.davis@innovatedesign.com',
      replyType: 'Question',
      subject: 'Re: Revolutionary marketing automation platform'
    },
    {
      recipient: 'emily.thompson@summittech.io',
      from: 'michael.chen@acmecorp.com',
      replyType: 'Meeting Request',
      subject: 'Re: AI-driven insights for your business growth'
    }
  ];

  emailReplyEvents.forEach((event, index) => {
    events.push({
      id: `webhook-reply-${index + 1}`,
      title: 'Email Reply Webhook Processed',
      description: `From: ${event.from} | ${event.replyType}`,
      activity_type: 'webhook_success',
      icon: 'message-circle',
      created_at: generateRandomTime(8 + index * 2), // 8, 10, 12 minutes ago
      recipient: event.recipient,
      status: event.replyType,
      reason: event.subject
    });
  });

  // Email Bounce Webhook events
  const emailBounceEvents = [
    {
      recipient: 'invalid@nonexistent.com',
      bounceType: 'Hard Bounce',
      reason: 'Mailbox does not exist'
    },
    {
      recipient: 'unknown@deleteddomain.com',
      bounceType: 'Hard Bounce',
      reason: 'Domain not found'
    },
    {
      recipient: 'full@oldcompany.com',
      bounceType: 'Soft Bounce',
      reason: 'Mailbox full'
    }
  ];

  emailBounceEvents.forEach((event, index) => {
    events.push({
      id: `webhook-bounce-${index + 1}`,
      title: 'Email Bounce Webhook Processed',
      description: `To: ${event.recipient} | ${event.bounceType}`,
      activity_type: 'webhook_success',
      icon: 'alert-triangle',
      created_at: generateRandomTime(15 + index * 5), // 15, 20, 25 minutes ago
      recipient: event.recipient,
      status: event.bounceType,
      reason: event.reason
    });
  });

  // Webhook Error events (fewer, to show some failures)
  const webhookErrorEvents = [
    {
      recipient: 'test@invalid-domain.xyz',
      errorType: 'Connection Timeout',
      reason: 'Webhook endpoint unreachable'
    },
    {
      recipient: 'bounce@spam-trap.com',
      errorType: 'Authentication Failed',
      reason: 'Invalid webhook signature'
    }
  ];

  webhookErrorEvents.forEach((event, index) => {
    events.push({
      id: `webhook-error-${index + 1}`,
      title: 'Webhook Processing Failed',
      description: `To: ${event.recipient} | ${event.errorType}`,
      activity_type: 'webhook_error',
      icon: 'x-circle',
      created_at: generateRandomTime(30 + index * 10), // 30, 40 minutes ago
      recipient: event.recipient,
      status: 'Failed',
      reason: event.reason
    });
  });

  // Sort by created_at (most recent first)
  return events.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};

export const DEMO_WEBHOOK_EVENTS: DemoWebhookEvent[] = generateDemoWebhookEvents();

// Helper function to get recent webhook events (last 24 hours)
export const getRecentDemoWebhooks = () => {
  const now = new Date();
  const yesterday = subDays(now, 1);
  
  return DEMO_WEBHOOK_EVENTS.filter(event => new Date(event.created_at) > yesterday);
};

// Helper function to get webhook stats
export const getDemoWebhookStats = () => {
  const total = DEMO_WEBHOOK_EVENTS.length;
  const successful = DEMO_WEBHOOK_EVENTS.filter(e => e.activity_type === 'webhook_success').length;
  const failed = DEMO_WEBHOOK_EVENTS.filter(e => e.activity_type === 'webhook_error').length;
  const successRate = total > 0 ? Math.round((successful / total) * 100) : 0;
  
  return {
    total,
    successful,
    failed,
    successRate,
    avgResponseTime: 245 // milliseconds
  };
};
